import { useRef, type CSSProperties, type ReactNode } from 'react';
import { Slot } from 'radix-ui';
import { cn } from '@worldcoo/ui/lib/utils';
import { useInView } from '@worldcoo/ui/hooks/use-in-view';

/**
 * Geometry and timing live in `new-expand` in `-styles/theme.css`. `asChild`, as these boxes are grid or
 * flex children and a `<div>` slipped in between would hand them a new, unstyled parent.
 */

/**
 * The stagger every hero uses. Not the home bento, which sits behind a `Suspense` boundary and
 * already arrives into a settled document. A bigger step lengthens `lead` too — they share a clock.
 */
export const EXPAND_STEP_ROOMY = 180;

export function ExpandGroup({
	lead = 0,
	step,
	asChild = false,
	className,
	children,
}: {
	/** In `Expand` index steps: the late start a group behind a `Suspense` boundary gets on its own. */
	lead?: number;
	/** Milliseconds; overrides the stylesheet default. */
	step?: number;
	asChild?: boolean;
	className?: string;
	children: ReactNode;
}) {
	// Typed without `asChild`, so one ref satisfies both branches; the observer takes any element.
	const ref = useRef<HTMLDivElement>(null);
	// Inset, so a group is not spent as a sliver at the bottom edge. One-way: this is an ENTRANCE.
	const arrived = useInView(ref, { margin: '-64px' });
	const Comp = asChild ? Slot.Root : 'div';

	// One trigger per GROUP, or the boxes' order would follow the reader's scroll speed.
	return (
		<Comp
			ref={ref}
			data-revealed={arrived || undefined}
			className={className}
			style={
				lead || step
					? ({
							...(lead ? { '--expand-lead': lead } : null),
							...(step ? { '--expand-step': `${step}ms` } : null),
						} as CSSProperties)
					: undefined
			}
		>
			{children}
		</Comp>
	);
}

export function Expand({
	index = 0,
	radius,
	asChild = false,
	className,
	children,
}: {
	/** One whole step per COLUMN; `1` and `1.5` make a stacked pair trail by half a column. */
	index?: number;
	/** When not `rounded-3xl`: a mismatched clip shows as the corners changing shape while the box grows. */
	radius?: string;
	asChild?: boolean;
	className?: string;
	children: ReactNode;
}) {
	const Comp = asChild ? Slot.Root : 'div';

	return (
		<Comp
			className={cn('new-expand', className)}
			style={{ '--expand-i': index, ...(radius ? { '--expand-radius': radius } : null) } as CSSProperties}
		>
			{children}
		</Comp>
	);
}
